// Доступ к протоколу (020: contracts/api.md, «Схемы»; data-model.md; research, Р1–Р8; ADR-0039):
// роли в протоколе, зона редактора, общий доступ, окно «Доступ к протоколу», приглашение
// и права пользователя на экране протокола. Решает всегда сервер: схемы только описывают ответы.
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { EmailInputSchema, FullNameInputSchema } from '../access/user.ts';
import { EmailStandingSchema, ExternalStateSchema } from '../access/email-policy.ts';

/**
 * Роль в протоколе (FR-003; 041, FR-007): «Читатель» или «Редактор»; у «Редактора» — необязательная
 * зона. Владелец — строка access.protocol_owners, не строка доступа (FR-002).
 */
export const protocolRoles = ['reader', 'editor'] as const;
export type ProtocolRole = (typeof protocolRoles)[number];
export const ProtocolRoleSchema = z.enum(protocolRoles);

/**
 * Роль в протоколе в старых событиях и записях журнала (041, research Р7): до 041 была отдельная
 * роль zone_editor — «Редактор» с зоной. Только для чтения.
 */
export const storedProtocolRoles = ['reader', 'editor', 'zone_editor'] as const;
export type StoredProtocolRole = (typeof storedProtocolRoles)[number];
export const StoredProtocolRoleSchema = z.enum(storedProtocolRoles);

/** Подпись роли и строка-пояснение в выборе роли (ux.md, глава 12). */
export const protocolRoleInfo = {
  reader: { title: 'Читатель', description: 'читает, печатает и выгружает' },
  editor: { title: 'Редактор', description: 'правит протокол; с зоной — только пункты зоны' },
} as const satisfies Record<ProtocolRole, { title: string; description: string }>;

/** Подпись роли из события или журнала: прежний zone_editor — «Редактор (зона)». */
export const storedProtocolRoleTitle = {
  reader: 'Читатель',
  editor: 'Редактор',
  zone_editor: 'Редактор (зона)',
} as const satisfies Record<StoredProtocolRole, string>;

/**
 * Ранг роли в протоколе (023: research, Р2): массовая выдача не понижает роль — «Читатель» ниже
 * «Редактора» с зоной, тот ниже «Редактора» без зоны (041: ключ zone_editor — редактор с зоной).
 */
export const protocolRoleRank = {
  reader: 1,
  zone_editor: 2,
  editor: 3,
} as const satisfies Record<StoredProtocolRole, number>;

/** Ранг выдачи по роли и зоне: «Редактор» с зоной — между читателем и редактором без зоны. */
export function grantRank(role: ProtocolRole, zone: readonly unknown[] | null | undefined): number {
  return role === 'editor' && (zone?.length ?? 0) > 0
    ? protocolRoleRank.zone_editor
    : protocolRoleRank[role];
}

/** Общий доступ (050): новый протокол — listed; отсутствие строки у старого — company. */
export const sharingModes = ['company', 'listed'] as const;
export type SharingMode = (typeof sharingModes)[number];
export const SharingModeSchema = z.enum(sharingModes);

/** Подпись и пояснение общего доступа; пояснение меняется вместе с выбором (ux.md, глава 12). */
export const sharingModeInfo = {
  company: {
    title: 'Все сотрудники компании — читатели',
    description:
      'Все сотрудники компании могут читать протокол. Права на изменение задаются отдельно.',
  },
  listed: {
    title: 'Участники протокола',
    description:
      'Владелец, ответственные по пунктам и добавленные сотрудники. Администраторы видят всё.',
  },
} as const satisfies Record<SharingMode, { title: string; description: string }>;

/** Записей зоны у «Редактора» с зоной (data-model.md, `access.protocol_grants`). */
export const ZONE_RECORDS_MIN = 1;
export const ZONE_RECORDS_MAX = 20;
/** Приглашений от одного пользователя за час (FR-005; ACC-006). */
export const INVITES_PER_HOUR = 30;
/** Кандидатов и записей зоны в ответе поиска окна. */
export const ACCESS_LOOKUP_LIMIT = 10;
/** Строка поиска окна «Доступ». */
export const ACCESS_QUERY_MIN = 2;
export const ACCESS_QUERY_MAX = 100;

/**
 * Запись зоны: организация или подразделение справочника (FR-004); 030 — человек: право «свои
 * пункты» в таблице — зона из одной записи человека (research Р2), её ставит только сервер.
 */
export const zoneRecordKinds = ['organization', 'unit', 'person'] as const;
export type ZoneRecordKind = (typeof zoneRecordKinds)[number];
export const ZoneRecordRefSchema = z.strictObject({
  kind: z.enum(zoneRecordKinds),
  id: IdSchema,
});
export type ZoneRecordRef = z.infer<typeof ZoneRecordRefSchema>;

/**
 * Что выбирают в зону редактора в окне «Доступ» и массовой выдаче: только организация
 * и подразделение (020, FR-004). Человек — зона «своих пунктов» (030), её не выдают.
 */
export const grantZoneRecordKinds = ['organization', 'unit'] as const;
export type GrantZoneRecordKind = (typeof grantZoneRecordKinds)[number];
export const GrantZoneRecordRefSchema = z.strictObject({
  kind: z.enum(grantZoneRecordKinds),
  id: IdSchema,
});
export type GrantZoneRecordRef = z.infer<typeof GrantZoneRecordRefSchema>;

/** Записи зоны для тела выдачи роли: без названий; человек (зона «своих пунктов») не выдаётся. */
export function grantZoneRefs(
  zone: readonly { readonly kind: ZoneRecordKind; readonly id: string }[],
): GrantZoneRecordRef[] {
  return zone.flatMap(({ kind, id }) => (kind === 'person' ? [] : [{ kind, id }]));
}

/** Запись зоны для показа: у подразделения — его организация. */
export const ZoneRecordSchema = z.object({
  kind: z.enum(zoneRecordKinds),
  id: IdSchema,
  name: z.string(),
  organization: z.string().nullable(),
  archived: z.boolean(),
});
export type ZoneRecord = z.infer<typeof ZoneRecordSchema>;

/** Ключ записи зоны в журнале действий: organization:<id>, unit:<id> — без названий. */
export function zoneRecordKey(record: ZoneRecordRef): string {
  return `${record.kind}:${record.id}`;
}

export const ZoneRefListSchema = z
  .array(GrantZoneRecordRefSchema)
  .min(ZONE_RECORDS_MIN)
  .max(ZONE_RECORDS_MAX);

export const ProtocolOwnerSchema = z.object({ userId: IdSchema, name: z.string() });
export type ProtocolOwner = z.infer<typeof ProtocolOwnerSchema>;

/** Строка «Сотрудники с доступом». Почта — только тем, кто управляет доступом. */
export const GrantSchema = z.object({
  grantId: IdSchema,
  userId: IdSchema,
  name: z.string(),
  email: z.string().nullable(),
  role: ProtocolRoleSchema,
  zone: z.array(ZoneRecordSchema),
  /** Пунктов зоны (действующих) — у «Редактора» с зоной в режиме ведения, иначе null. */
  zoneItemCount: z.number().int().min(0).nullable(),
  /** Пароля ещё нет, ссылка-приглашение действует. */
  invitePending: z.boolean(),
  version: z.number().int().min(1),
  /** 022: метка «Внешний» (active) или «Внешний доступ выключен» (disabled); сотрудник — null. */
  external: ExternalStateSchema.nullable(),
});
export type Grant = z.infer<typeof GrantSchema>;

/** Окно «Доступ к протоколу» (FR-001). */
export const ProtocolAccessViewSchema = z.object({
  owner: ProtocolOwnerSchema.nullable(),
  sharing: SharingModeSchema,
  sharingVersion: z.number().int().min(0),
  canManage: z.boolean(),
  grants: z.array(GrantSchema),
});
export type ProtocolAccessView = z.infer<typeof ProtocolAccessViewSchema>;

/** Уровень пользователя в протоколе: что экран разрешает (research, Р6). */
export const accessLevels = ['manage', 'edit', 'zone', 'read'] as const;
export type AccessLevel = (typeof accessLevels)[number];

/** Права пользователя на экране протокола: в состоянии и в чтении изменений. */
export const ProtocolAccessSchema = z.object({
  /** Хеш уровня, роли, зоны и замыкания: чтение изменений отдаёт права только при другом ключе. */
  key: z.string(),
  level: z.enum(accessLevels),
  /** Роль в протоколе; owner — ведущий протокола; null — право от ролей установки или общего доступа. */
  role: z.union([z.literal('owner'), ProtocolRoleSchema]).nullable(),
  /** Зона — у «Редактора» с зоной: правка только по зоне (level = zone). */
  zone: z
    .object({
      records: z.array(ZoneRecordSchema),
      /** Номера записей справочника замыкания (люди и организации) — без имён. */
      closure: z.array(IdSchema),
    })
    .nullable(),
  owner: ProtocolOwnerSchema.nullable(),
  /** Общий доступ «Только добавленные». */
  listed: z.boolean(),
  /**
   * Записи справочника из протокола (027: FR-019, FR-025; research, Р4): `create` — короткая форма
   * в поповере, `edit` — правка в карточке; оба — directory.write в области протокола. Ответ до 027
   * поля не содержит — нет права.
   */
  records: z.object({ create: z.boolean(), edit: z.boolean() }).optional(),
});
export type ProtocolAccess = z.infer<typeof ProtocolAccessSchema>;

const AccessLookupQuerySchema = z.object({
  q: z.string().trim().min(ACCESS_QUERY_MIN).max(ACCESS_QUERY_MAX),
});

export const AccessCandidatesQuerySchema = AccessLookupQuerySchema;
export const AccessCandidatesResponseSchema = z.object({
  items: z
    .array(
      z.object({
        id: IdSchema,
        name: z.string(),
        email: z.string(),
        defaultZone: ZoneRecordSchema.nullable(),
        hasAccess: z.boolean(),
        /** 022: метка внешнего пользователя; сотрудник — null. */
        external: ExternalStateSchema.nullable(),
      }),
    )
    .max(ACCESS_LOOKUP_LIMIT),
  /**
   * q — полная почта, пользователя с ней нет: ФИО и зона из справочника (FR-006). 022: кем
   * считается адрес (standing); у внешнего зона по умолчанию — его организация (EXT-03).
   */
  invitee: z
    .object({
      email: z.string(),
      name: z.string().nullable(),
      defaultZone: ZoneRecordSchema.nullable(),
      standing: EmailStandingSchema,
    })
    .nullable(),
});
export type AccessCandidatesResponse = z.infer<typeof AccessCandidatesResponseSchema>;

export const ZoneOptionsQuerySchema = AccessLookupQuerySchema;
export const ZoneOptionsResponseSchema = z.object({
  items: z
    .array(
      ZoneRecordSchema.extend({
        /** Пунктов зоны в протоколе окна; поиск без протокола (массовое окно, 023) — null. */
        itemCount: z.number().int().min(0).nullable(),
      }),
    )
    .max(ACCESS_LOOKUP_LIMIT),
});
export type ZoneOptionsResponse = z.infer<typeof ZoneOptionsResponseSchema>;

/** 041 (FR-007): зона необязательна у «Редактора» и запрещена «Читателю». */
function zoneMatchesRole(body: { role?: ProtocolRole; zone?: readonly unknown[] | null }): boolean {
  if (body.role === undefined) return true;
  return body.role === 'editor' || body.zone === undefined || body.zone === null;
}
const zoneRoleMessage = {
  message: 'Зона задаётся только редактору',
  path: ['zone'],
};

export const GrantBodySchema = z
  .strictObject({
    userId: IdSchema,
    role: ProtocolRoleSchema,
    zone: ZoneRefListSchema.optional(),
    notify: z.boolean(),
  })
  .refine(zoneMatchesRole, zoneRoleMessage);
export type GrantBody = z.infer<typeof GrantBodySchema>;

/** Смена роли или зоны; zone: null — «Снять зону» (041, FR-009). */
export const GrantUpdateBodySchema = z
  .strictObject({
    role: ProtocolRoleSchema.optional(),
    zone: ZoneRefListSchema.nullable().optional(),
  })
  .refine((body) => body.role !== undefined || body.zone !== undefined, {
    message: 'Нечего сохранять',
  })
  .refine((body) => body.role === undefined || zoneMatchesRole(body), zoneRoleMessage);
export type GrantUpdateBody = z.infer<typeof GrantUpdateBodySchema>;

export const SharingBodySchema = z.strictObject({ sharing: SharingModeSchema });

/** If-Match смены общего доступа: версия строки `access.protocol_sharing`, строки ещё нет — 0. */
export const SharingIfMatchHeadersSchema = z.object({
  'if-match': z.coerce.number().int().min(0),
});
export type SharingBody = z.infer<typeof SharingBodySchema>;

export const InviteBodySchema = z
  .strictObject({
    email: EmailInputSchema,
    fullName: FullNameInputSchema.optional(),
    role: ProtocolRoleSchema,
    zone: ZoneRefListSchema.optional(),
  })
  .refine(zoneMatchesRole, zoneRoleMessage);
export type InviteBody = z.infer<typeof InviteBodySchema>;

export const GrantParamsSchema = z.strictObject({ id: IdSchema, grantId: IdSchema });

export const InviteResponseSchema = z.object({
  view: ProtocolAccessViewSchema,
  userId: IdSchema,
  /** Новый пользователь создан приглашением. */
  created: z.boolean(),
});
export type InviteResponse = z.infer<typeof InviteResponseSchema>;

export const ResendResponseSchema = z.object({ queued: z.literal(true) });

/**
 * GET /api/protocols/mine (022: research, Р11): протоколы, где у пользователя есть роль в протоколе,
 * — «Открытые вам протоколы» на главной внешнего. По номеру П-N.
 */
export const MineResponseSchema = z.object({
  items: z.array(
    z.object({
      id: IdSchema,
      number: z.number().int().min(1),
      title: z.string(),
      role: ProtocolRoleSchema,
      /** Режим протокола — как documentModes (`minutes/protocol.ts` импортирует этот файл). */
      mode: z.enum(['file', 'maintained']),
    }),
  ),
});
export type MineResponse = z.infer<typeof MineResponseSchema>;
